import React from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Table from '../../components/table/table';
import { economicActivities } from '../../mocks/EconomicActivities';
import { ivaStatuses } from '../../mocks/IvaStatuses';
import Checkbox from '../../components/checkBox/CheckBox';
import { useClientTheme } from '../../context/ThemeContext';

interface ClientData {
  id: number;
  cuit: string;
  verified: boolean;
  has_valid_corporate_composition: boolean;
  basic_data: {
    company_name: string;
    company_address: string;
    state: string;
    city: string;
    phone_number: string;
    email: string;
    legal_entity: string;
    activity: string;
    iva_status: string;
    date_incorporation: string;
    economic_group: string | null;
  };
  corporate_structure: Array<{
    ci: string;
    first_name: string;
    last_name: string;
    ownership_interest: string;
    position: string;
    assets: string;
    value: number;
  }>;
}

interface GeneralDataTabProps {
  clientData: ClientData;
}

const GeneralDataTab: React.FC<GeneralDataTabProps> = ({ clientData }) => {

  const { clientTheme } = useClientTheme();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR');
  };

  const getActivityDescription = (activityCode: string) => {
    const activity = economicActivities.find(a => String(a.code) === String(activityCode));
    return activity ? `${activityCode} - ${activity.label}` : activityCode;
  };

  const getIvaStatusDescription = (ivaCode: string) => {
    const ivaStatus = ivaStatuses.find(status => String(status.code) === String(ivaCode));
    return ivaStatus ? ivaStatus.label : ivaCode;
  };

  const partnersColumns = [
    {
      name: 'Nombre',
      selector: (row: any) => row.first_name,
      cell: (row: any) => <span>{row.first_name}</span>,
    },
    {
      name: 'Documento',
      selector: (row: any) => row.ci,
      cell: (row: any) => <span>{row.ci}</span>,
    },
    {
      name: 'Cargo',
      selector: (row: any) => row.position,
      cell: (row: any) => <span>{row.position}</span>,
    },
    {
      name: 'Participación',
      selector: (row: any) => row.ownership_interest,
      cell: (row: any) => <span>{row.ownership_interest}%</span>,
    },
    {
      name: 'Bienes',
      selector: (row: any) => row.assets,
      cell: (row: any) => <span>{row.assets || '-'}</span>,
    },
    {
      name: 'Valor',
      selector: (row: any) => row.value,
      cell: (row: any) => <span>{row.value ? `$${row.value.toLocaleString()}` : '-'}</span>,
    },
  ];

  const InfoField = ({ label, value }: { label: string; value: string }) => (
    <Card margin={{ bottom: '3' }} display="flex" gap="8px" alignItems="center">
      <P type="2" variant="neutral" weight="bold" modifier="strong" withMargin={false}>
        {label}:
      </P>
      <P type="2" variant="neutral-1" weight="regular" modifier="regular" withMargin={false}>
        {value || '-'}
      </P>
    </Card>
  );

  return (
    <Card spacing="4">
      {/* Company Name Title */}
      <Card margin={{ bottom: '4' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          {clientData.basic_data.company_name}
        </H>
      </Card>

      {/* Company Information - Three Column Layout */}
      <Card display="flex" gap="24px" margin={{ bottom: '4' }}>
        {/* Column 1 */}
        <Card width="33.33%">
          <InfoField 
            label="Personería" 
            value={clientData.basic_data.legal_entity} 
          />
          <InfoField 
            label="Actividad económica" 
            value={getActivityDescription(clientData.basic_data.activity)} 
          />
          <InfoField 
            label="Domicilio fiscal" 
            value={clientData.basic_data.company_address} 
          />
        </Card>

        {/* Column 2 */}
        <Card width="33.33%">
          <InfoField 
            label="Fecha de constitución" 
            value={formatDate(clientData.basic_data.date_incorporation)} 
          />
          <InfoField 
            label="Localidad" 
            value={clientData.basic_data.city} 
          />
          <InfoField 
            label="Correo electrónico" 
            value={clientData.basic_data.email} 
          />
        </Card>

        {/* Column 3 */}
        <Card width="33.33%">
          <InfoField 
            label="Condición frente al IVA" 
            value={getIvaStatusDescription(clientData.basic_data.iva_status)} 
          />
          <InfoField 
            label="Provincia" 
            value={clientData.basic_data.state} 
          />
          <InfoField 
            label="Teléfono" 
            value={clientData.basic_data.phone_number} 
          />
        </Card>
      </Card>

      {/* Partners Section */}
      <Card margin={{ top: '5' }}>
        {/* Section Header */}

        <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'} display='flex' justifyContent='space-between'>
            <H withMargin={false} type='3' weight='bold' modifier='strong'>
                Socios
            </H>
        </Card>
        <Card
        display='flex'
        justifyContent='flex-end'
        gap='4px'
        margin={{ top: '2' }}
        >
        <Checkbox
            checked={clientData.has_valid_corporate_composition}
            disabled={true}
            id='summary_has_valid_corporate_composition-checkbox'
            size='md'
        >
            La información se ajusta a los documentos
        </Checkbox>
        </Card>

        {/* Partners Table */}
        <Card margin={{ top: '3' }}>
          <Table
            columns={partnersColumns}
            data={clientData.corporate_structure}
            tableData={{ page: 0, size: clientData.corporate_structure.length, totalElements: clientData.corporate_structure.length, totalPages: 1 }}
            variant="weak"
          />
        </Card>
      </Card>
    </Card>
  );
};

export default GeneralDataTab;